export const CATALOG_FILTERS_QUERY_PARAM = "filters";
export const CATALOG_FILTERS_URL_STATE_VERSION = 1;

export interface CatalogFacet {
  name: string;
  values: string[];
}

export interface CatalogCategoryNode {
  name: string;
  path: string;
}

export interface MerchantCatalogFilterUrlState {
  v: typeof CATALOG_FILTERS_URL_STATE_VERSION;
  q?: string;
  facets?: CatalogFacet[];
  selectedLeafPaths?: string[];
  categoryNavigationStack?: CatalogCategoryNode[];
}

export interface MerchantCatalogSearchHrefArgs {
  catalogName: string;
  merchantName?: string;
  basePath?: string;
  q?: string | null;
  facets?: CatalogFacet[] | null;
  selectedLeafPaths?: string[];
  categoryNavigationStack?: CatalogCategoryNode[];
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function normalizeFacets(value: unknown): CatalogFacet[] | undefined {
  if (!Array.isArray(value)) return undefined;

  const facets = value
    .map((facet): CatalogFacet | null => {
      if (!facet || typeof facet !== "object") return null;
      const record = facet as Record<string, unknown>;
      if (typeof record.name !== "string" || !isStringArray(record.values)) return null;
      return { name: record.name, values: record.values };
    })
    .filter((facet): facet is CatalogFacet => facet !== null && facet.values.length > 0);

  return facets.length > 0 ? facets : undefined;
}

function normalizeCategoryNavigationStack(value: unknown): CatalogCategoryNode[] | undefined {
  if (!Array.isArray(value)) return undefined;

  const stack = value
    .map((node): CatalogCategoryNode | null => {
      if (!node || typeof node !== "object") return null;
      const record = node as Record<string, unknown>;
      if (typeof record.name !== "string" || typeof record.path !== "string") return null;
      return { name: record.name, path: record.path };
    })
    .filter((node): node is CatalogCategoryNode => node !== null);

  return stack.length > 0 ? stack : undefined;
}

export function parseMerchantCatalogFilterUrlState(
  params: URLSearchParams | ReadonlyURLSearchParams
): MerchantCatalogFilterUrlState | null {
  const raw = params.get(CATALOG_FILTERS_QUERY_PARAM);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (parsed.v !== CATALOG_FILTERS_URL_STATE_VERSION) return null;

    return {
      v: CATALOG_FILTERS_URL_STATE_VERSION,
      q: typeof parsed.q === "string" ? parsed.q : undefined,
      facets: normalizeFacets(parsed.facets),
      selectedLeafPaths: isStringArray(parsed.selectedLeafPaths)
        ? parsed.selectedLeafPaths
        : undefined,
      categoryNavigationStack: normalizeCategoryNavigationStack(parsed.categoryNavigationStack),
    };
  } catch {
    return null;
  }
}

export function serializeMerchantCatalogFilterUrlState(
  state: MerchantCatalogFilterUrlState
): string | null {
  const trimmedQuery = state.q?.trim() ?? "";
  const hasQuery = trimmedQuery.length > 0;
  const hasFacets = (state.facets?.length ?? 0) > 0;
  const hasSelectedLeaves = (state.selectedLeafPaths?.length ?? 0) > 0;
  const hasNavigationStack = (state.categoryNavigationStack?.length ?? 0) > 0;

  if (!hasQuery && !hasFacets && !hasSelectedLeaves && !hasNavigationStack) {
    return null;
  }

  return JSON.stringify({
    v: CATALOG_FILTERS_URL_STATE_VERSION,
    q: hasQuery ? trimmedQuery : undefined,
    facets: hasFacets ? state.facets : undefined,
    selectedLeafPaths: hasSelectedLeaves ? state.selectedLeafPaths : undefined,
    categoryNavigationStack: hasNavigationStack ? state.categoryNavigationStack : undefined,
  });
}

export function buildMerchantCatalogSearchHref({
  catalogName,
  merchantName,
  basePath,
  q,
  facets,
  selectedLeafPaths,
  categoryNavigationStack,
}: MerchantCatalogSearchHrefArgs): string {
  const hrefBase =
    basePath ??
    (merchantName
      ? `/${encodeURIComponent(merchantName)}/catalogs/${encodeURIComponent(catalogName)}`
      : `/catalogs/${encodeURIComponent(catalogName)}`);
  const filterState: MerchantCatalogFilterUrlState = {
    v: CATALOG_FILTERS_URL_STATE_VERSION,
    q: q ?? undefined,
    facets: facets ?? undefined,
    selectedLeafPaths,
    categoryNavigationStack,
  };
  const serialized = serializeMerchantCatalogFilterUrlState(filterState);
  if (!serialized) return hrefBase;

  const params = new URLSearchParams();
  params.set(CATALOG_FILTERS_QUERY_PARAM, serialized);
  return `${hrefBase}?${params.toString()}`;
}

type ReadonlyURLSearchParams = Pick<URLSearchParams, "get">;
